import { create } from 'zustand'
import type { Capability } from '../utils/permissions'

/** Jen cesta uvnitř aplikace („/…“); cokoli jiného = neurčeno. */
const validHomePage = (path: string | null): string | null => (path && path.startsWith('/') ? path : null)

/** Odpověď GET /auth/permissions. */
export interface PermissionsPayload {
  capabilities: Capability[]
  is_admin: boolean
  role: { id: string | null; name: string; home_page: string | null }
  /** Zrušeno — server posílá vždy []; ignoruje se. */
  hidden_features?: string[]
  /** Testovací instance (test.das-elektromontaze.cz) — štítek TEST a přehrání DB z produkce. */
  test_instance?: boolean
}

interface PermissionsState {
  /** Efektivní schopnosti přihlášeného uživatele. Prázdné = ještě nenačteno. */
  capabilities: Capability[]
  /** Superuživatel (role s příznakem is_admin) — má vždy vše. */
  isAdmin: boolean
  /** Úvodní stránka role (cesta ve frontendu); null = rozcestník. */
  homePage: string | null
  /** Role přihlášeného uživatele. */
  roleId: string | null
  roleName: string | null
  /** Komu oprávnění patří — `can()` je použije jen pro tohoto uživatele. */
  userId: string | null
  /** Zrušené skrývání funkcí — vždy prázdné (moduly řídí matice rolí). Zůstává kvůli odběratelům. */
  hiddenFeatures: string[]
  /** Běží testovací instance (ne produkce). */
  testInstance: boolean
  /** True po prvním dokončeném pokusu o načtení /auth/permissions (i neúspěšném). */
  loaded: boolean
  setPermissions: (payload: PermissionsPayload, userId: string | null) => void
  setHomePage: (homePage: string | null) => void
  markLoaded: () => void
  clear: () => void
}

/**
 * Oprávnění přihlášeného uživatele. Plní se po přihlášení, po impersonaci a po úpravách v administraci.
 * `can()` v utils/permissions je čte; do načtení padá zpět na výchozí matici podle starého klíče role.
 */
export const usePermissionsStore = create<PermissionsState>((set) => ({
  capabilities: [],
  isAdmin: false,
  homePage: null,
  roleId: null,
  roleName: null,
  userId: null,
  hiddenFeatures: [],
  testInstance: false,
  loaded: false,
  setPermissions: (payload, userId) => set({
    capabilities: payload.capabilities ?? [],
    isAdmin: !!payload.is_admin,
    homePage: validHomePage(payload.role?.home_page ?? null),
    roleId: payload.role?.id ?? null,
    roleName: payload.role?.name ?? null,
    userId,
    hiddenFeatures: [],
    testInstance: !!payload.test_instance,
  }),
  setHomePage: (homePage) => set({ homePage: validHomePage(homePage) }),
  markLoaded: () => set({ loaded: true }),
  clear: () => set({
    capabilities: [], isAdmin: false, homePage: null, roleId: null, roleName: null, userId: null,
    hiddenFeatures: [], testInstance: false, loaded: false,
  }),
}))
